import React, { useEffect, useState } from "react";
import {
    fetchAdvancedMedicalEquipmentCategoryList,
    fetchAdvancedMedicalEquipmentDetailsList,
    addAdvancedMedicalEquipmentDetails,
    editAdvancedMedicalEquipmentDetails,
    updateAdvancedMedicalEquipmentDetails,
    trashAdvancedMedicalEquipmentDetails,
} from "../../Adapters/ApiAdapter";
import { baseImage_path } from "../../variables/Variables";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export const AdvancedMedicalEquipmentDetailsList = () => {
    const [rows, setRows] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null); // null = add, object = edit
    const [saving, setSaving] = useState(false);

    // helpers --------------------------------------------------------------
    const flatten = (apiData) => {
        const flat = [];
        apiData.forEach((cat) => {
            cat.details.forEach((d) => {
                flat.push({
                    id: d.equipment_id,
                    category_id: cat.category_id,
                    category_name: cat.category_name,
                    heading: d.equipment_heading,
                    content: d.equipment_content,
                    img_path: d.equipment_img_path,
                });
            });
        });
        return flat.sort((a, b) => b.id - a.id);
    };

    const load = async () => {
        setLoading(true);
        try {
            const [catRes, detRes] = await Promise.all([
                fetchAdvancedMedicalEquipmentCategoryList(),
                fetchAdvancedMedicalEquipmentDetailsList(),
            ]);
            setCategories(catRes.data);
            setRows(flatten(detRes.data));
        } catch {
            toast.error("Failed to load equipment details");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const openAdd = () => {
        setEditing({
            equipment_category_id: "",
            heading: "",
            content: "",
            img_path: null,
        });
        setDialogOpen(true);
    };

    // const openEdit = async (id) => {
    //     try {
    //         const res = await editAdvancedMedicalEquipmentDetails(id);
    //         const d = res.data?.data || {};
    //         setEditing({
    //             id: d.equipment_id,
    //             equipment_category_id: d.equipment_category_id,
    //             heading: d.equipment_heading,
    //             content: d.equipment_content,
    //             img_path: d.equipment_img_path,
    //         });
    //         setDialogOpen(true);
    //     } catch {
    //         toast.error("Failed to fetch equipment data");
    //     }
    // };

    const openEdit = async (id) => {
        try {
            const res = await editAdvancedMedicalEquipmentDetails(id);
            const wrapper = res.data?.equipment_details;
            const d = wrapper?.details?.[0];

            if (!d) {
                toast.error("Equipment details missing from API response");
                return;
            }

            setEditing({
                id: d.equipment_id,
                equipment_category_id: wrapper.category_id,
                heading: d.equipment_heading,
                content: d.equipment_content,
                img_path: d.equipment_img_path,
            });

            setDialogOpen(true);
        } catch {
            toast.error("Failed to fetch equipment data");
        }
    };

    const handleSave = async () => {
        if (!editing.heading || !editing.content || !editing.equipment_category_id) {
            toast.error("All fields are required");
            return;
        }
        setSaving(true);
        const formData = new FormData();
        formData.append("heading", editing.heading);
        formData.append("content", editing.content);
        formData.append("equipment_category_id", editing.equipment_category_id);
        if (editing.img_path instanceof File) formData.append("img_path", editing.img_path);

        try {
            if (editing.id) {
                await updateAdvancedMedicalEquipmentDetails(editing.id, formData);
                toast.success("Equipment updated");
            } else {
                await addAdvancedMedicalEquipmentDetails(formData);
                toast.success("Equipment added");
            }
            setDialogOpen(false);
            load();
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const handleTrash = async (id) => {
        if (!window.confirm("Trash this equipment?")) return;
        try {
            await trashAdvancedMedicalEquipmentDetails(id);
            toast.success("Equipment trashed");
            load();
        } catch {
            toast.error("Failed to trash equipment");
        }
    };

    const columns = [
        { field: "id", headerName: "ID", width: 80 },
        { field: "category_name", headerName: "Category", width: 180 },
        { field: "heading", headerName: "Heading", flex: 1 },
        {
            field: "img_path",
            headerName: "Image",
            width: 120,
            renderCell: (params) => (
                params.value ? (
                    <img
                        src={`${baseImage_path}/${params.value}`}
                        alt="equip"
                        style={{ width: 80, height: 60, objectFit: "cover", borderRadius: 4 }}
                    />
                ) : (
                    "—"
                )
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 200,
            renderCell: (params) => (
                <Box>
                    <Button size="small" variant="contained" onClick={() => openEdit(params.id)}>
                        Edit
                    </Button>
                    <Button
                        size="small"
                        color="error"
                        variant="contained"
                        sx={{ ml: 1 }}
                        onClick={() => handleTrash(params.id)}
                    >
                        Trash
                    </Button>
                </Box>
            ),
        },
    ];

    return (
        <Box m={2}>
            <ToastContainer />
            <Typography variant="h4" mb={2}>
                Hospital Technology – Equipment Details
            </Typography>
            <Button variant="contained" onClick={openAdd} sx={{ mb: 2 }}>
                Add Equipment
            </Button>
            {loading ? (
                <CircularProgress />
            ) : (
                <DataGrid autoHeight rows={rows} columns={columns} pageSize={10} />
            )}

            {/* Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing?.id ? "Edit" : "Add"} Equipment</DialogTitle>
                <DialogContent>
                    <TextField
                        select
                        label="Category"
                        fullWidth
                        margin="normal"
                        SelectProps={{ native: true }}
                        value={editing?.equipment_category_id || ""}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => setEditing({ ...editing, equipment_category_id: e.target.value })}
                    >
                        {categories.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.category_name}
                            </option>
                        ))}
                    </TextField>

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Heading"
                        value={editing?.heading || ""}
                        onChange={(e) => setEditing({ ...editing, heading: e.target.value })}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Content"
                        multiline
                        minRows={4}
                        value={editing?.content || ""}
                        onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                    />

                    {/* Image upload & preview */}
                    <Button variant="contained" component="label" sx={{ mt: 2 }}>
                        Upload Image
                        <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) setEditing({ ...editing, img_path: file });
                            }}
                        />
                    </Button>
                    {editing?.img_path && (
                        <Box mt={2}>
                            <img
                                src={
                                    editing.img_path instanceof File
                                        ? URL.createObjectURL(editing.img_path)
                                        : `${baseImage_path}/${editing.img_path}`
                                }
                                alt="preview"
                                style={{ width: 120, height: 80, objectFit: "cover", borderRadius: 4 }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? <CircularProgress size={24} /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};